import { Checkbox } from '@mantine/core';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getOSFullName } from '../../../utils/os';
import { Avatar, Button, NewTabLink } from '../../Atoms';
import type { InstanceOption } from '../InstanceFamilyOptionList/InstanceFamilyOptionList';

interface CloudDeploymentSummaryCardProps {
    backupEstimatedCost: string;
    computeHourlyTotalPrice: number;
    diskAdditionEstimatedCost: string;
    diskAdditionGb: number;
    hostname: string;
    hourlyTotalPrice: number;
    isBackupEnabled: boolean;
    isDeployLoading?: boolean;
    isDiskAdditionEnabled: boolean;
    monthlyTotalPrice: number;
    onDeploy: () => void;
    quantity: number;
    selectedInstanceFamily: 'shared' | 'general-purpose' | 'dedicated';
    selectedInstanceOption?: InstanceOption;
    selectedVersion: string | null;
    osType: 'linux' | 'windows';
    /** Rendered above the terms checkbox, e.g. `DeployTargetNotice`. */
    notice?: ReactNode;
    /** Set when the caller may not deploy here: the button stays focusable, described by this id. */
    deployBlockedReasonId?: string;
}

function getSelectedInstanceFamilyLabel(
    selectedInstanceFamily: 'shared' | 'general-purpose' | 'dedicated',
    t: (key: string) => string
) {
    if (selectedInstanceFamily === 'shared') {
        return t('deploymentSummaryCard.sharedCompute');
    }

    if (selectedInstanceFamily === 'general-purpose') {
        return t('deploymentSummaryCard.generalPurposeCompute');
    }

    return t('deploymentSummaryCard.dedicatedCompute');
}

export default function CloudDeploymentSummaryCard({
    backupEstimatedCost,
    computeHourlyTotalPrice,
    diskAdditionEstimatedCost,
    diskAdditionGb,
    hostname,
    hourlyTotalPrice,
    isBackupEnabled,
    isDeployLoading = false,
    isDiskAdditionEnabled,
    monthlyTotalPrice,
    onDeploy,
    quantity,
    selectedInstanceFamily,
    selectedInstanceOption,
    selectedVersion,
    osType,
    notice,
    deployBlockedReasonId,
}: CloudDeploymentSummaryCardProps) {
    const { t } = useTranslation('components');
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const deployPrice = selectedInstanceOption ? computeHourlyTotalPrice.toFixed(2) : '—';

    return (
        <div className="sticky top-4 self-start w-full h-auto">
            <div className="w-full h-auto rounded-md bg-surface-1 border-[0.5px] border-line overflow-hidden">
                <div
                    style={{
                        background: 'var(--summary-hero-gradient)',
                    }}
                    className="w-full h-auto p-4 flex flex-col justify-start items-start gap-3.5 border-b-[0.5px] border-line"
                >
                    <div className="inline-flex text-xs items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        <span className="uppercase text-fg/80">
                            {t('deploymentSummaryCard.readyToDeploy')}
                        </span>
                    </div>
                    <div className="flex flex-row justify-start items-center gap-3">
                        <Avatar value={`${hostname}`} size={'48'} />
                        <div className="flex flex-col justify-start items-start min-w-0">
                            <span className="text-lg font-mono font-bold break-all">
                                {hostname.length === 0
                                    ? t('deploymentSummaryCard.undefined')
                                    : hostname}
                            </span>
                            <div className="inline-flex flex-wrap items-center text-sm text-fg/68 gap-x-1">
                                <span>{t('deploymentSummaryCard.region')}</span>
                                <span className="text-2xl leading-0">·</span>
                                <span>
                                    {getSelectedInstanceFamilyLabel(selectedInstanceFamily, t)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="p-4 grid grid-cols-1 items-center gap-5">
                    <div className="flex flex-row justify-between items-start gap-3">
                        <div className="flex flex-col justify-start items-start gap-1 min-w-0">
                            <div className="text-[14px] text-fg/68">
                                <span>
                                    {getSelectedInstanceFamilyLabel(selectedInstanceFamily, t)}
                                </span>
                            </div>
                            <div className="inline-flex items-center font-bold text-sm gap-0.5 text-fg">
                                <span>{selectedInstanceOption?.sku || '—'}</span>
                                <span className="text-xs text-fg/68">
                                    &nbsp; {t('deploymentSummaryCard.quantitySuffix', { quantity })}
                                </span>
                            </div>
                            <div className="inline-flex flex-wrap items-center text-[13px] text-fg/68 gap-x-1">
                                <span>
                                    {selectedInstanceOption?.vcpu || '—'}{' '}
                                    {t('deploymentSummaryCard.vcpu')}
                                </span>
                                <span className="text-xl leading-0">·</span>
                                <span>
                                    {selectedInstanceOption?.memory || '—'}{' '}
                                    {t('deploymentSummaryCard.ram')}
                                </span>
                                <span className="text-xl leading-0">·</span>
                                <span>
                                    {selectedInstanceOption?.ssd || '—'}{' '}
                                    {t('deploymentSummaryCard.ssdNvme')}
                                </span>
                            </div>
                        </div>
                        <div className="flex flex-row justify-end items-center text-nowrap shrink-0">
                            <span className="text-[15px] font-bold">
                                {selectedInstanceOption ? computeHourlyTotalPrice.toFixed(2) : '—'}฿
                            </span>
                            <span className="text-sm text-fg/68">
                                {t('deploymentSummaryCard.hourlySuffix')}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-row justify-between items-start gap-3">
                        <div className="flex flex-col justify-start items-start gap-1 min-w-0">
                            <div className="inline-flex items-center font-bold text-sm gap-0.5 text-fg">
                                <span>
                                    {selectedVersion ? getOSFullName(selectedVersion) || '—' : '—'}
                                </span>
                            </div>
                            <div className="inline-flex flex-wrap items-center text-[13px] text-fg/68 gap-x-1">
                                <span>
                                    {osType === 'linux'
                                        ? t('deploymentSummaryCard.linuxDistribution')
                                        : t('deploymentSummaryCard.windows')}
                                </span>
                                <span className="text-xl leading-0">·</span>
                                {osType === 'linux' ? (
                                    <span>{t('deploymentSummaryCard.openSource')}</span>
                                ) : (
                                    <span className="text-yellow-500/80 light:text-yellow-800">
                                        {t('deploymentSummaryCard.notIncludedLicense')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col justify-end items-end gap-1.5">
                            <span className="text-sm text-green-300 light:text-green-800">
                                {t('deploymentSummaryCard.included')}
                            </span>
                        </div>
                    </div>
                    {isDiskAdditionEnabled && (
                        <div className="flex flex-row justify-between items-start gap-3">
                            <div className="flex flex-col justify-start items-start gap-1 min-w-0">
                                <div className="inline-flex items-center font-bold text-sm gap-0.5 text-fg">
                                    <span>{t('deploymentSummaryCard.additionalDisk')}</span>
                                </div>
                                <div className="inline-flex flex-wrap items-center text-[13px] text-fg/68 gap-x-1">
                                    <span>{diskAdditionGb} GB</span>
                                    <span className="text-xl leading-0">·</span>
                                    <span>{t('deploymentSummaryCard.ssdNvme')}</span>
                                </div>
                            </div>
                            <div className="flex flex-row justify-end items-end text-nowrap shrink-0">
                                <span className="text-sm font-bold">
                                    {diskAdditionEstimatedCost}฿
                                </span>
                                <span className="text-sm text-fg/68">
                                    {t('deploymentSummaryCard.hourlySuffix')}
                                </span>
                            </div>
                        </div>
                    )}
                    {isBackupEnabled && (
                        <div className="flex flex-row justify-between items-start gap-3">
                            <div className="flex flex-col justify-start items-start gap-1 min-w-0">
                                <div className="inline-flex items-center font-bold text-sm gap-0.5 text-fg">
                                    <span>{t('deploymentSummaryCard.dailyBackups')}</span>
                                </div>
                                <div className="inline-flex flex-wrap items-center text-[13px] text-fg/68 gap-x-1">
                                    <span>{t('deploymentSummaryCard.retention3Days')}</span>
                                    <span className="text-xl leading-0">·</span>
                                    <span>{t('deploymentSummaryCard.customizable')}</span>
                                </div>
                                <div className="inline-flex items-center text-[13px] text-yellow-500/80 light:text-yellow-800 gap-1">
                                    <span>{t('deploymentSummaryCard.backupEstimatedNote')}</span>
                                </div>
                            </div>
                            <div className="flex flex-row justify-end items-end text-nowrap shrink-0">
                                <span className="text-sm font-bold">{backupEstimatedCost}฿</span>
                                <span className="text-sm text-fg/68">
                                    {t('deploymentSummaryCard.hourlySuffix')}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1.5 border-t-[0.5px] border-line">
                    <div className="w-full flex flex-col justify-start items-start gap-1.5">
                        <div className="w-full flex flex-row justify-between items-center">
                            <span className="text-sm text-fg/68">
                                {t('deploymentSummaryCard.dueHourly')}
                            </span>
                            <span className="font-bold text-fg">
                                {selectedInstanceOption ? hourlyTotalPrice.toFixed(2) : '—'}฿
                            </span>
                        </div>
                        <div className="w-full flex flex-row justify-between items-center">
                            <span className="text-sm text-fg/68">
                                {t('deploymentSummaryCard.dueMonthly')}
                            </span>
                            <span className="font-bold text-fg">
                                {selectedInstanceOption ? monthlyTotalPrice.toFixed(2) : '—'}฿
                            </span>
                        </div>
                    </div>

                    {notice}

                    <Checkbox
                        size="xs"
                        checked={isTermsAccepted}
                        onChange={(event) => setIsTermsAccepted(event.currentTarget.checked)}
                        label={
                            <span className="text-[13px] text-fg">
                                {t('deploymentSummaryCard.termsOfServicePrefix')}{' '}
                                <NewTabLink
                                    href={'https://xver.cloud/th/legal/terms-of-service/'}
                                    className="text-blue-400 light:text-blue-700 hover:underline"
                                >
                                    {t('deploymentSummaryCard.termsOfServiceLink')}
                                </NewTabLink>
                            </span>
                        }
                        color="blue"
                        variant="filled"
                    />

                    <Button
                        width={'100%'}
                        className="mt-1"
                        disabled={!isTermsAccepted || isDeployLoading}
                        isLoading={isDeployLoading}
                        aria-disabled={deployBlockedReasonId ? true : undefined}
                        aria-describedby={deployBlockedReasonId}
                        style={
                            deployBlockedReasonId
                                ? { opacity: 0.5, cursor: 'not-allowed' }
                                : undefined
                        }
                        onClick={deployBlockedReasonId ? undefined : onDeploy}
                    >
                        {quantity > 1
                            ? t('deploymentSummaryCard.deployServersHourly', { price: deployPrice })
                            : t('deploymentSummaryCard.deployServerHourly', { price: deployPrice })}
                    </Button>
                </div>
            </div>
        </div>
    );
}
